import React from "react";
import styled, { createGlobalStyle } from "styled-components/macro";
import { TTheme } from "../../theme/defaultTheme";
import { Button, EButtonType } from "../Button";
import { Icon } from "../Icon";
import { TypoBodyRegular, TypoH2, TypoSubheader } from "../typography";
import { useSystem } from "../../hooks/useSystem";
import SessionService from "../../services/SessionService";

const HideBodyOverflow = createGlobalStyle`
  body {
    overflow: hidden;
  }
`;

interface ModalProps {
  setTimer: () => void;
  resetTimer: () => void;
}

export const Modal = (props: ModalProps) => {
  const system = useSystem();
  const handleLogout = () => {
    SessionService.logout();
  };

  const handleProlong = async () => {
    props.resetTimer();
    try {
      const { expired } = await SessionService.getSessionDuration(system);
      if (!expired) {
        await SessionService.prolongSession(system);
        props.setTimer();
        SessionService.hideIframe();
      } else {
        SessionService.redirectToTiles();
      }
    } catch (error) {
      SessionService.logWarn(error, "Couldn't prolong session");
      SessionService.redirectToTiles();
    }
  };

  return (
    <Mask>
      <HideBodyOverflow />
      <Wrapper onClick={e => e.stopPropagation()}>
        <ModalContent>
          <StyledIcon name="logout" width={120} />
          <StyledTypoH2>Twoja sesja wkrótce wygaśnie</StyledTypoH2>
          <TypoBodyRegular>
            Ze względu na brak aktywności nastąpi automatyczne wylogowanie
          </TypoBodyRegular>
        </ModalContent>
        <Separator />
        <ButtonsContainer>
          <StyledButton
            onClick={e => {
              e.stopPropagation();
              handleLogout();
            }}
            text={"Wyloguj"}
            buttonType={EButtonType.secondary}
          />

          <Button
            onClick={e => {
              e.stopPropagation();
              handleProlong();
            }}
            text={"Przedłuż sesję"}
          />
        </ButtonsContainer>
      </Wrapper>
    </Mask>
  );
};

const Mask = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 999;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:before {
    content: "";
    z-index: -1;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0.6;
    background-color: ${({ theme }: { theme: TTheme }) => theme.colors.gray100};
  }
`;

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.gray10};
  max-width: 640px;
  margin-top: -240px;
  width: 100%;
  display: block;
  box-shadow: ${({ theme }) => theme.shadows.elevation2};
`;

const ModalContent = styled(TypoSubheader)`
  padding: 32px 24px;
  max-height: calc(100vh - 250px);
  white-space: break-spaces;
  text-align: center;
  flex-direction: column;
  word-wrap: break-word;
  overflow: auto;
`;

const StyledIcon = styled(Icon)`
  margin-bottom: 24px;
`;

const StyledTypoH2 = styled(TypoH2)`
  color: ${({ theme }) => theme.colors.primary100};
  margin-bottom: 24px;
`;

const Separator = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.gray30};
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 16px 0;
`;

const StyledButton = styled(Button)`
  margin-right: 16px;
`;
